import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Eye, Filter, ShoppingCart, Radio } from 'lucide-react';
import {
  fetchOrders,
  fetchOrdersCount,
  fetchZones,
} from '../services/api.ts';
import './Orders.css';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const [filterZone, setFilterZone] = useState<number | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [zones, setZones] = useState<any[]>([]);
  const [liveConnected, setLiveConnected] = useState(false);
  const [liveEvents, setLiveEvents] = useState<string[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    fetchZones(0, 100).then(setZones).catch(console.error);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const [list, countRes] = await Promise.all([
        fetchOrders(offset, perPage, filterZone, filterStatus),
        fetchOrdersCount(filterZone, filterStatus),
      ]);
      setOrders(list);
      setTotal(countRes.count);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterZone, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  // SSE live updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const params = new URLSearchParams({ token });
    if (filterZone) params.set('zone_id', String(filterZone));
    const url = `${baseUrl}/orders/live?${params.toString()}`;

    let es: EventSource | null = null;
    let retryTimeout: ReturnType<typeof setTimeout>;

    const connect = () => {
      es = new EventSource(url);
      eventSourceRef.current = es;

      es.onopen = () => setLiveConnected(true);
      es.onerror = () => {
        setLiveConnected(false);
        es?.close();
        retryTimeout = setTimeout(connect, 5000);
      };
      es.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (payload.event === 'connected') return;
          if (payload.event === 'order_created' || payload.event === 'order_updated') {
            // Show toast
            const status = payload.data?.status || '';
            const msg = payload.event === 'order_created'
              ? `New order ${payload.data?.order_number || ''}`
              : `Order #${payload.data?.id} → ${status.replace('_', ' ')}`;
            setLiveEvents(prev => [msg, ...prev].slice(0, 5));
            // Reload data
            loadData();
          }
        } catch { /* ignore parse errors */ }
      };
    };

    connect();

    return () => {
      clearTimeout(retryTimeout);
      es?.close();
      eventSourceRef.current = null;
      setLiveConnected(false);
    };
  }, [filterZone, loadData]);

  const filtered = orders.filter((o) => {
    const s = `${o.order_number} ${o.customer || ''} ${o.postcode || ''} ${o.town || ''}`.toLowerCase();
    return s.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(total / perPage);

  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-loading"><div className="orders-spinner" /><p>Loading orders...</p></div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <div className="orders-header-left">
          <ShoppingCart size={22} />
          <h1>Orders</h1>
          <span className="orders-count">{total}</span>
          <span className={`orders-live-badge ${liveConnected ? 'connected' : ''}`}>
            <Radio size={12} /> {liveConnected ? 'Live' : 'Connecting…'}
          </span>
        </div>
      </div>

      {/* Live event toasts */}
      {liveEvents.length > 0 && (
        <div className="orders-live-toast-bar">
          {liveEvents.map((msg, i) => (
            <div key={i} className="orders-live-toast">{msg}</div>
          ))}
        </div>
      )}

      <div className="orders-filter-bar">
        <div className="orders-search-bar">
          <Search size={18} />
          <input type="text" placeholder="Search by order number, customer, postcode..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="orders-filter-group">
          <Filter size={16} />
          <select value={filterZone ?? ''} onChange={e => { setFilterZone(e.target.value ? Number(e.target.value) : undefined); setCurrentPage(1); }}>
            <option value="">All Zones</option>
            {zones.map((z: any) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <select value={filterStatus ?? ''} onChange={e => { setFilterStatus(e.target.value || undefined); setCurrentPage(1); }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="assigned">Assigned</option>
            <option value="picked_up">Picked Up</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {error && <div className="orders-error">{error}</div>}

      <div className="orders-table-wrap">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Customer</th>
              <th>Location</th>
              <th>Zone</th>
              <th>Driver</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} className="orders-empty">No orders found</td></tr>
            ) : filtered.map((o) => (
              <tr key={o.id}>
                <td className="orders-cell-order">{o.order_number}</td>
                <td>{o.customer || '\u2014'}</td>
                <td>{[o.town, o.postcode].filter(Boolean).join(', ') || '\u2014'}</td>
                <td>{o.zone?.name || '\u2014'}</td>
                <td>{o.driver?.name || '\u2014'}</td>
                <td className="orders-cell-amount">£{o.total_amount?.toFixed(2)}</td>
                <td>
                  <span className={`orders-badge orders-badge-${o.status}`}>
                    {o.status?.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  {o.payment_status ? (
                    <span className={`orders-badge ${o.payment_status === 'succeeded' ? 'orders-badge-success' : 'orders-badge-warning'}`}>
                      {o.payment_status}
                    </span>
                  ) : '\u2014'}
                </td>
                <td>{o.created_at ? new Date(o.created_at).toLocaleDateString() : '\u2014'}</td>
                <td>
                  <button className="orders-action-btn" title="View" onClick={() => navigate(`/dashboard/orders/${o.id}`)}>
                    <Eye size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="orders-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Orders;
